import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  SetlistItemType,
  SetlistSection,
  SetlistVisibilityMode,
  Song,
} from '@stage/contracts';
import { ApiError } from '../api/client.js';
import { VersionConflictError } from '../api/showClient.js';
import { useShowClient } from '../hooks/useShowClient.js';
import {
  addItem,
  addSection,
  moveItem,
  removeItem,
  removeSection,
  renameSection,
  updateItem,
} from '../lib/setlist.js';

/**
 * Set-list editor (§17.6). Edit sections and items, reorder via keyboard-accessible up/down buttons
 * (a required alternative to drag-and-drop, §35), set per-item type/title/song/public-visibility/
 * requestability, choose the gig's public {@link SetlistVisibilityMode}, and save through the
 * versioned `PUT` with optimistic concurrency. A stale write surfaces a reload prompt (§17.6).
 */

const VISIBILITY_MODES: ReadonlyArray<{ value: SetlistVisibilityMode; label: string }> = [
  { value: 'hidden', label: 'Hidden' },
  { value: 'section_names_only', label: 'Section names only' },
  { value: 'current_only', label: 'Current only' },
  { value: 'current_and_next', label: 'Current and next' },
  { value: 'remaining_in_current_set', label: 'Remaining in current set' },
  { value: 'full_upcoming', label: 'Full upcoming' },
  { value: 'full_with_played_history', label: 'Full with played history' },
];

const ITEM_TYPES: ReadonlyArray<{ value: SetlistItemType; label: string }> = [
  { value: 'song', label: 'Song' },
  { value: 'improv_slot', label: 'Improv slot' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'break', label: 'Break' },
  { value: 'custom', label: 'Custom' },
];

export function SetlistEditorPage() {
  const { gigId = '' } = useParams();
  const client = useShowClient();

  const [sections, setSections] = useState<SetlistSection[]>([]);
  const [visibilityMode, setVisibilityMode] =
    useState<SetlistVisibilityMode>('current_and_next');
  const [version, setVersion] = useState(0);
  const [songs, setSongs] = useState<Song[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');

  const load = useCallback(async () => {
    setLoadError(null);
    setConflict(false);
    try {
      const setlist = await client.getSetlist(gigId);
      setSections(setlist.sections);
      setVisibilityMode(setlist.visibilityMode);
      setVersion(setlist.version);
      setDirty(false);
      setLoaded(true);
      // Song list (for the "link a song" picker) is scoped to the set list's artist. A failure
      // here is non-fatal — items can still be edited without a linked song.
      try {
        const songsPage = await client.listSongs(setlist.artistId);
        setSongs(songsPage.items);
      } catch {
        setSongs([]);
      }
    } catch (err) {
      if (err instanceof ApiError) setLoadError(err.message);
      else setLoadError('Could not load the set list.');
    }
  }, [client, gigId]);

  useEffect(() => {
    void load();
  }, [load]);

  const mutate = useCallback((next: SetlistSection[]) => {
    setSections(next);
    setDirty(true);
  }, []);

  const onSave = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setSaving(true);
      setSaveError(null);
      setConflict(false);
      try {
        const saved = await client.putSetlist(gigId, version, { sections, visibilityMode });
        setSections(saved.sections);
        setVisibilityMode(saved.visibilityMode);
        setVersion(saved.version);
        setDirty(false);
      } catch (err) {
        if (err instanceof VersionConflictError) {
          setConflict(true);
        } else if (err instanceof ApiError) {
          setSaveError(err.message);
        } else {
          setSaveError('Could not save the set list.');
        }
      } finally {
        setSaving(false);
      }
    },
    [client, gigId, version, sections, visibilityMode],
  );

  if (loadError && !loaded) {
    return (
      <main className="page">
        <p className="error">{loadError}</p>
        <button type="button" onClick={() => void load()}>
          Retry
        </button>
        <Link className="link" to={`/app/gigs/${gigId}`}>
          Back to gig
        </Link>
      </main>
    );
  }
  if (!loaded) {
    return (
      <main className="page" aria-busy="true">
        <p>Loading set list…</p>
      </main>
    );
  }

  return (
    <main className="page page--wide">
      <header className="app-bar">
        <div>
          <p className="eyebrow">
            <Link className="link" to={`/app/gigs/${gigId}`}>
              Gig editor
            </Link>{' '}
            / Set list
          </p>
          <h1>Set list</h1>
        </div>
        <Link className="button-link" to={`/app/gigs/${gigId}/console`}>
          Open console
        </Link>
      </header>

      {conflict && (
        <div className="validation" role="alert">
          <p className="error">This set list was changed by someone else.</p>
          <button type="button" onClick={() => void load()}>
            Reload latest
          </button>
        </div>
      )}

      <form onSubmit={onSave} className="setlist-editor">
        <section className="card">
          <label>
            Public visibility mode
            <select
              value={visibilityMode}
              onChange={(e) => {
                setVisibilityMode(e.target.value as SetlistVisibilityMode);
                setDirty(true);
              }}
            >
              {VISIBILITY_MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <p className="help">Controls what the audience page reveals of the set list (§17.3).</p>
        </section>

        {sections.length === 0 && (
          <p className="muted">No sections yet. Add one below to start building the set.</p>
        )}

        {sections.map((section) => (
          <section key={section.sectionId} className="card setlist-section">
            <div className="setlist-section__head">
              <label className="setlist-section__name">
                <span className="visually-hidden">Section name</span>
                <input
                  value={section.name}
                  placeholder="Section name"
                  onChange={(e) => mutate(renameSection(sections, section.sectionId, e.target.value))}
                />
              </label>
              <button
                type="button"
                className="danger"
                onClick={() => mutate(removeSection(sections, section.sectionId))}
              >
                Remove section
              </button>
            </div>

            {section.items.length === 0 ? (
              <p className="muted">No items.</p>
            ) : (
              <ul className="setlist-items">
                {section.items.map((item, index) => (
                  <li key={item.itemId} className="setlist-item">
                    <div className="setlist-item__order">
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={`Move ${item.displayTitle || 'item'} up`}
                        onClick={() => mutate(moveItem(sections, item.itemId, 'up'))}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={`Move ${item.displayTitle || 'item'} down`}
                        onClick={() => mutate(moveItem(sections, item.itemId, 'down'))}
                      >
                        ↓
                      </button>
                      <span className="setlist-item__num" aria-hidden="true">
                        {index + 1}
                      </span>
                    </div>

                    <div className="setlist-item__fields">
                      <label>
                        <span className="visually-hidden">Item type</span>
                        <select
                          value={item.type}
                          onChange={(e) =>
                            mutate(
                              updateItem(sections, item.itemId, {
                                type: e.target.value as SetlistItemType,
                              }),
                            )
                          }
                        >
                          {ITEM_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="setlist-item__title">
                        <span className="visually-hidden">Display title</span>
                        <input
                          value={item.displayTitle}
                          placeholder="Display title"
                          onChange={(e) =>
                            mutate(
                              updateItem(sections, item.itemId, { displayTitle: e.target.value }),
                            )
                          }
                        />
                      </label>
                      {item.type === 'song' && (
                        <label>
                          <span className="visually-hidden">Linked song</span>
                          <select
                            value={item.songId ?? ''}
                            onChange={(e) =>
                              mutate(
                                updateItem(sections, item.itemId, {
                                  songId: (e.target.value || null) as typeof item.songId,
                                }),
                              )
                            }
                          >
                            <option value="">— link a song —</option>
                            {songs.map((s) => (
                              <option key={s.songId} value={s.songId}>
                                {s.title}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                      <div className="setlist-item__toggles">
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={item.publicVisible}
                            onChange={(e) =>
                              mutate(
                                updateItem(sections, item.itemId, {
                                  publicVisible: e.target.checked,
                                }),
                              )
                            }
                          />
                          Public
                        </label>
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={item.requestable}
                            onChange={(e) =>
                              mutate(
                                updateItem(sections, item.itemId, {
                                  requestable: e.target.checked,
                                }),
                              )
                            }
                          />
                          Requestable
                        </label>
                        <button
                          type="button"
                          className="link"
                          onClick={() => mutate(removeItem(sections, item.itemId))}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="button-row setlist-section__add">
              {ITEM_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  className="ghost"
                  onClick={() => mutate(addItem(sections, section.sectionId, t.value))}
                >
                  + {t.label}
                </button>
              ))}
            </div>
          </section>
        ))}

        <section className="card add-section">
          <label className="add-section__name">
            Add a section
            <input
              value={newSectionName}
              placeholder="e.g. Set 2, Encore"
              onChange={(e) => setNewSectionName(e.target.value)}
            />
          </label>
          <button
            type="button"
            onClick={() => {
              mutate(addSection(sections, newSectionName.trim()));
              setNewSectionName('');
            }}
          >
            Add section
          </button>
        </section>

        <div className="setlist-save button-row">
          <button type="submit" className="action--primary" disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save set list'}
          </button>
          <span className="muted" aria-live="polite">
            {dirty ? 'Unsaved changes' : `Saved · version ${version}`}
          </span>
          {saveError && <span className="error">{saveError}</span>}
        </div>
      </form>
    </main>
  );
}
